import React from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Keep lastAction state outside component to persist across profile changes
let globalLastAction = "waitlist";

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: center;
`;

const IconButtonsGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledIconButton = muiStyled(IconButton, {
  shouldForwardProp: prop => prop !== 'status'
})(({ theme, status }) => ({
  padding: 0,
  borderRadius: 3,
  backgroundColor: '#2c2c2c',
  color: 'white',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  '&:hover': {
    backgroundColor: '#000000'
  }
}));

const StyledButtonGroup = muiStyled(ButtonGroup)`
  width: 100%;
  & .MuiButtonGroup-grouped {
    border: none !important;
    min-width: unset;
    
    &:hover {
      border: none !important;
    }
  }
`;

const getBackgroundColor = (status, isActive) => {
  switch (status) {
    case 'accepted': return isActive ? '#00a75e' : '#00c26f';
    case 'rejected': return isActive ? '#ff7e68' : '#ff9e8e';
    case 'banned': return isActive ? '#000000' : '#2c2c2c';
    case 'waitlist': return isActive ? '#3C3C3C' : '#4c4c4c';
    default: return '#00a75e';
  }
};

const MainButton = muiStyled(Button, {
  shouldForwardProp: prop => !['status', 'isActive'].includes(prop)
})(({ theme, status, isActive }) => ({
  flex: 1,
  border: 'none !important',
  boxShadow: 'none',
  padding: '8px 15px',
  height: 40,
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0.2,
  backgroundColor: getBackgroundColor(status, isActive),
  color: 'white',
  '&:hover': {
    backgroundColor: getBackgroundColor(status, true),
    boxShadow: 'none',
    border: 'none',
    cursor: 'default'
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    marginRight: 8
  },
  '& .button-content': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap'
  }
}));

const DropdownButton = muiStyled(Button, {
  shouldForwardProp: prop => !['status', 'isActive'].includes(prop)
})(({ theme, status, isActive }) => ({
  padding: '0 8px',
  backgroundColor: getBackgroundColor(status, isActive),
  color: 'white',
  border: 'none !important',
  height: 40,
  '&:hover': {
    backgroundColor: getBackgroundColor(status, true),
    boxShadow: 'none',
    border: 'none',
    cursor: 'default'
  }
}));

const StyledPopper = muiStyled(Popper)`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 5px;
    padding-right: 5px;
    `;

const StyledMenuList = muiStyled(MenuList)`
    border-radius: 3px;
    padding: 10px 5px;
    width: fit-content;
`;

const StyledPaper = muiStyled(Paper)`
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  margin-bottom: 5px;
  width: fit-content;
`;

const StyledMenuItem = muiStyled(MenuItem)`
  padding: 8px 15px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2px;
  width: fit-content;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  .MuiSvgIcon-root {
    font-size: 16px;
    margin-right: 8px;
  }
  
  .menu-item-content {
    display: flex;
    align-items: center;
  }
`;

const WaitlistSplitButton = ({ content, onUpdateStatus }) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const options = [
    { 
      label: 'Accept ?', 
      value: 'accepted',
      icon: <CheckCircleIcon />,
      activeText: 'Accepted'
    },
    { 
      label: 'Waitlist ?', 
      value: 'waitlist',
      icon: <AccessTimeIcon />,
      activeText: 'Waitlisted'
    },
    { 
      label: 'Reject ?', 
      value: 'rejected',
      icon: <ThumbDownIcon />,
      activeText: 'Rejected'
    },
    { 
      label: 'Ban User?', 
      value: 'banned',
      icon: <BlockIcon />,
      activeText: 'Banned'
    }
  ];

  const handleClick = (value) => {
    globalLastAction = value;
    onUpdateStatus(content?.id, content?.organization_id, value);
  };

  const handleMenuItemClick = (value) => {
    handleClick(value);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const currentOption = options.find(opt => opt.value === content?.status) || 
                       options.find(opt => opt.value === globalLastAction) ||
                       options[0];

  const buttonText = content?.status === currentOption.value ? 
                    currentOption.activeText : 
                    currentOption.label;

  return (
    <ButtonContainer className="ButtonContainer">
      <IconButtonsGroup className="IconButtonsGroup">
        <Tooltip className="Tooltip" title="Add to Waitlist">
          <StyledIconButton
            className="StyledIconButton"
            onClick={() => handleClick('waitlist')}
            status="waitlist"
          >
            ✋🏽
          </StyledIconButton>
        </Tooltip>
        <Tooltip title="Reject">
          <StyledIconButton
            onClick={() => handleClick('rejected')}
            status="rejected"
          >
            👎🏽
          </StyledIconButton>
        </Tooltip>
        <Tooltip title="Ban User">
          <StyledIconButton
            onClick={() => handleClick('banned')}
            status="banned"
          >
            ⛔
          </StyledIconButton>
        </Tooltip>
      </IconButtonsGroup>
      <StyledButtonGroup
        className="StyledButtonGroup"
        ref={anchorRef}
      >
        <MainButton
          className="MainButton"
          onClick={() => handleClick(currentOption.value)}
          isActive={content?.status === currentOption.value}
          status={currentOption.value}
        >
          <span className="button-content">
            {currentOption.icon}
            {buttonText}
          </span>
        </MainButton>
        <DropdownButton
          size="small"
          onClick={handleToggle}
          isActive={content?.status === currentOption.value}
          status={currentOption.value}
        >
          <ArrowDropDownIcon />
        </DropdownButton>
      </StyledButtonGroup>
      
      <StyledPopper
        className="Popper"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        style={{
          zIndex: 1,
          width: anchorRef.current?.offsetWidth
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <StyledPaper className="StyledPaper">
              <ClickAwayListener onClickAway={handleClose}>
                <StyledMenuList className="StyledMenuList">
                  {options.map(option => (
                    <StyledMenuItem 
                      key={option.value}
                      onClick={() => handleMenuItemClick(option.value)}
                      selected={option.value === content?.status}
                    >
                      <span className="menu-item-content">
                        {option.icon}
                        {option.label}
                      </span>
                    </StyledMenuItem>
                  ))}
                </StyledMenuList>
              </ClickAwayListener>
            </StyledPaper>
          </Fade>
        )}
      </StyledPopper>
    </ButtonContainer>
  );
};

export default WaitlistSplitButton;